import { Edit } from "lucide-react";
import { Button } from "../../../../../components/ui/button";
import { Card, CardContent } from "../../../../../components/ui/card";
import {
  SimpleColumn,
  SimpleSortableTable,
} from "../../../../../components/ui/simple-sortable-table";
import { Company } from "../types";

interface CompaniesTableProps {
  companies: Company[];
  onEditClick: (company: Company) => void;
}

export function CompaniesTable({
  companies,
  onEditClick,
}: CompaniesTableProps) {
  if (companies.length === 0) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-foreground">
              Nenhuma empresa cadastrada
            </h2>
            <p className="text-muted-foreground mt-2">
              Cadastre a primeira empresa usando o formulário acima
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const columns: SimpleColumn<Company>[] = [
    {
      key: "cnpj",
      label: "CNPJ",
      cellClassName: "font-medium",
    },
    {
      key: "razaoSocial",
      label: "Razão Social",
    },
    {
      key: "apelido",
      label: "Apelido (OPS)",
      render: (company) => (
        <span className="inline-flex items-center rounded-full px-2 py-1 text-xs font-medium bg-primary/10 text-primary">
          {company.apelido}
        </span>
      ),
    },
    {
      key: "emailContato",
      label: "Email Contato",
    },
    {
      key: "cep",
      label: "CEP",
    },
    {
      key: "endereco",
      label: "Endereço",
    },
    {
      key: "franquiaUuid",
      label: "Franquia UUID",
      render: (company) =>
        company.franquiaUuid ? (
          <span className="text-xs text-muted-foreground">
            {company.franquiaUuid}
          </span>
        ) : (
          <span className="text-muted-foreground">-</span>
        ),
    },
    {
      key: "redutor",
      label: "Redutor",
      sortValue: (company) => company.redutor ?? 1,
      render: (company) => (
        <span className="font-semibold text-primary">
          {company.redutor !== undefined ? company.redutor.toFixed(2) : "1.00"}
        </span>
      ),
    },
    {
      key: "nfsAtivo",
      label: "NFS Ativo",
      sortValue: (company) => Boolean(company.nfsAtivo),
      render: (company) => (
        <span
          className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
            company.nfsAtivo
              ? "bg-green-100 text-green-800"
              : "bg-red-100 text-red-800"
          }`}
        >
          {company.nfsAtivo ? "Ativo" : "Inativo"}
        </span>
      ),
    },
    {
      key: "ofertaAtiva",
      label: "Oferta",
      sortValue: (company) => company.ofertaAtiva !== false,
      render: (company) => (
        <span
          className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
            company.ofertaAtiva !== false
              ? "bg-green-100 text-green-800"
              : "bg-red-100 text-red-800"
          }`}
        >
          {company.ofertaAtiva !== false ? "Ativa" : "Pausada"}
        </span>
      ),
    },
    {
      key: "criadoPor",
      label: "Criado Por",
    },
    {
      key: "dataCriacao",
      label: "Data Criação",
    },
    {
      key: "acoes",
      label: "Ações",
      sortable: false,
      headClassName: "text-right",
      cellClassName: "text-right",
      render: (company) => (
        <Button size="sm" variant="outline" onClick={() => onEditClick(company)}>
          <Edit className="h-4 w-4 mr-1" />
          Editar
        </Button>
      ),
    },
  ];

  return (
    <Card>
      <CardContent className="pt-6">
        <h2 className="text-2xl font-bold text-foreground mb-4">
          Empresas Cadastradas ({companies.length})
        </h2>
        <div className="overflow-x-auto">
          <SimpleSortableTable
            columns={columns}
            rows={companies}
            rowKey={(company) => company.id}
          />
        </div>
      </CardContent>
    </Card>
  );
}
